import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, HOME_FOR_ROLE } from '../../context/AuthContext';
import { Button, Field } from '../../components/ui';
import { useToast } from '../../components/Toast';
import OtpVerify from '../../components/OtpVerify';
import BrandMark from '../../components/BrandMark';

export default function Login() {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);
  // Set when login is blocked on an account that never finished verifying —
  // we already sent them a fresh code, so drop straight into entering it.
  const [pendingEmail, setPendingEmail] = useState(null);

  if (user) return <Navigate to={HOME_FOR_ROLE[user.role]} replace />;

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const me = await login(form.email, form.password);
      if (!HOME_FOR_ROLE[me.role]) {
        logout();
        toast.error(t('auth.login.teamAccountError'));
        return;
      }
      navigate(location.state?.from || HOME_FOR_ROLE[me.role], { replace: true });
    } catch (err) {
      if (err.payload?.needsVerification) {
        setPendingEmail(err.payload.email || form.email);
      } else {
        toast.error(err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  function onVerified(me) {
    navigate(location.state?.from || HOME_FOR_ROLE[me.role], { replace: true });
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-brand">
          <BrandMark />
          <strong>Nannies to the Rescue</strong>
        </Link>

        {pendingEmail ? (
          <OtpVerify
            email={pendingEmail}
            onVerified={onVerified}
            lede={t('auth.login.notVerifiedLede', { email: pendingEmail })}
          />
        ) : (
          <>
            <h1>{t('auth.login.welcomeBack')}</h1>
            <p className="muted">{t('auth.login.subtitle')}</p>

            <form onSubmit={onSubmit} noValidate>
              <Field label={t('auth.login.email')} required>
                <input
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </Field>
              <Field label={t('auth.login.password')} required>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                />
              </Field>
              <p className="auth-forgot">
                <Link to="/forgot-password">{t('auth.login.forgotPassword')}</Link>
              </p>
              <Button type="submit" loading={busy}>{t('auth.login.signIn')}</Button>
            </form>

            <p className="auth-alt">
              {t('auth.login.newHere')} <Link to="/register/family">{t('auth.login.registerFamily')}</Link> {t('auth.login.or')}{' '}
              <Link to="/register/nanny">{t('auth.login.applyNanny')}</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
